import { push } from "connected-react-router";
import { actionCreatorAsync, actionCreator } from "./actionHelpers";
import * as api from "../api/ipd";
import { replaceActiveIpd, setActiveBed } from "../slices/bedManagementSlice";
import { AppThunk } from "../store";
import { IPD, IMedicine } from "../interfaces/IpdInterface";
import { IpdFilter } from "../containers/IPD/Wards/WardAdmissions";

export enum Type {
  GET_IPD_RECORDS = "GET_IPD_RECORDS",
  GET_IPD_RECORD = "GET_IPD_RECORD",
  CREATE_IPD_RECORD = "CREATE_IPD_RECORD",
  UPDATE_IPD_RECORD = "UPDATE_IPD_RECORD",
  DELETE_IPD_RECORD = "DELETE_IPD_RECORD",
  CREATE_DISCHARGE_SUMMARY = "CREATE_DISCHARGE_SUMMARY",

  CREATE_IPD_PROGRESS_RECORD = "CREATE_IPD_PROGRESS_RECORD",
  CREATE_IPD_PRESCRIBE_MEDICATION = "CREATE_IPD_PRESCRIBE_MEDICATION"
}

export const setSelectedIpdRecord = actionCreator(Type.GET_IPD_RECORD);

export const getIpdRecords =
  ({
    filter,
    page,
    pageSize
  }: {
    filter: IpdFilter;
    page: number | string;
    pageSize: number | string;
  }): AppThunk =>
  async (dispatch) => {
    await dispatch(
      actionCreatorAsync(Type.GET_IPD_RECORDS, async () => {
        const res = await api.getIpds({ filter, page, pageSize });
        return res.data;
      })
    );
  };

export const createIpdRecord =
  (data: Partial<IPD>): AppThunk =>
  async (dispatch) => {
    await dispatch(
      actionCreatorAsync(Type.CREATE_IPD_RECORD, async () => {
        const res = await api.postIpd(data);
        return res.data;
      })
    );
  };

export const saveIpdRecord =
  (ipdData: Partial<IPD>, stayOnPage = false, isEmergency = false): AppThunk =>
  async (dispatch) => {
    const listBasePath = isEmergency ? "/emergency/clients" : "/ipd";

    if (ipdData.id) {
      await dispatch(
        actionCreatorAsync(Type.UPDATE_IPD_RECORD, async () => {
          const { data } = await api.patchIpd(ipdData.id, ipdData);
          if (data.id && !stayOnPage)
            dispatch(push(`${listBasePath}/${data.id}?filter=${IpdFilter.ADMITTED}`));
          return data;
        })
      );
    } else {
      let createdData = null;
      await dispatch(
        actionCreatorAsync(Type.CREATE_IPD_RECORD, async () => {
          const { data } = await api.postIpd(ipdData);
          createdData = data;
          dispatch(setActiveBed(data));
          if (data.id && !stayOnPage)
            dispatch(push(`${listBasePath}/${data.id}?filter=${IpdFilter.ADMITTED}`));
          return data;
        })
      );
      return createdData;
    }
    return undefined;
  };

export const createDischargeSummary =
  (ipdData: Partial<IPD>, isEmergency = false): AppThunk =>
  async (dispatch) => {
    const listBasePath = isEmergency ? "/emergency/clients" : "/ipd";

    await dispatch(
      actionCreatorAsync(Type.CREATE_DISCHARGE_SUMMARY, async () => {
        const res = await api.createDischargeSummary(ipdData.id, ipdData);
        dispatch(replaceActiveIpd(res.data));
        dispatch(setSelectedIpdRecord(res.data));
        dispatch(push(`${listBasePath}/${res.data.id}?filter=${IpdFilter.DISCHARGED}`));
        return res.data;
      })
    );
  };

export const createIpdProgressRecord =
  (ipdData: Partial<IPD>): AppThunk =>
  async (dispatch) => {
    await dispatch(
      actionCreatorAsync(Type.CREATE_IPD_PROGRESS_RECORD, async () => {
        const res = await api.createProgressRecord(ipdData.id, ipdData);
        dispatch(replaceActiveIpd(res.data));
        return res.data;
      })
    );
  };

export const createIpdPrescribeMedication =
  (id: number, medications: Array<IMedicine>): AppThunk =>
  async (dispatch) => {
    await dispatch(
      actionCreatorAsync(Type.CREATE_IPD_PRESCRIBE_MEDICATION, async () =>
        api.prescribedMedications(id, medications)
      )
    );
  };
